import * as THREE from "three";
import type { GeoPoint } from "../data/types";
import { latLngToPlane } from "./projection";

/**
 * Flat-map arc geometry.
 *
 * Endpoints project to the XY plane via Natural Earth. The arc lifts into +Z
 * by a fraction of the 2D chord length — short hops stay low, long hauls
 * (Lagos → NY) arc dramatically overhead.
 *
 * Uses a quadratic Bezier sampled as a Catmull-Rom curve so the tube geometry
 * has continuous tangents.
 */
export function buildArcCurveFlat(
  origin: GeoPoint,
  dest: GeoPoint,
  segments = 56,
): { curve: THREE.CatmullRomCurve3; length: number; chord: number } {
  const [ox, oy] = latLngToPlane(origin.lat, origin.lng);
  const [dx, dy] = latLngToPlane(dest.lat, dest.lng);

  const chord = Math.hypot(dx - ox, dy - oy);
  // Apex height: short hops barely lift; long hops rise ~0.45 of chord length.
  // The 0.22 floor keeps even adjacent-city arcs visibly 3D.
  const apexZ = Math.max(0.08, Math.min(0.95, chord * 0.38));

  const start = new THREE.Vector3(ox, oy, 0);
  const end = new THREE.Vector3(dx, dy, 0);
  const apex = new THREE.Vector3((ox + dx) / 2, (oy + dy) / 2, apexZ);

  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const omt = 1 - t;
    pts.push(
      new THREE.Vector3(
        omt * omt * start.x + 2 * omt * t * apex.x + t * t * end.x,
        omt * omt * start.y + 2 * omt * t * apex.y + t * t * end.y,
        omt * omt * start.z + 2 * omt * t * apex.z + t * t * end.z,
      ),
    );
  }

  const curve = new THREE.CatmullRomCurve3(pts, false, "centripetal");
  return { curve, length: chord, chord };
}

/** Convenience for callers that still want lat/lng → Vector3 on a plane. */
export function latLngToVec3Plane(lat: number, lng: number, z = 0): THREE.Vector3 {
  const [x, y] = latLngToPlane(lat, lng);
  return new THREE.Vector3(x, y, z);
}
